export const SCREEN_PX_PER_HOUR = 96
export const PRINT_MAX_BODY_PX = 520
export const PRINT_MIN_PX_PER_HOUR = 44
export const MIN_SPAN_MIN = 240
export const DEFAULT_DURATION_MIN = 30
export const MAX_DURATION_MIN = 12 * 60
export const MAX_LANES = 4

export const RAIL_WIDTH = 56
export const PERIOD_WIDTH = 104
export const MIN_DAY_WIDTH = 132
export const HEADER_GAP = 12

const MINUTES_IN_DAY = 24 * 60

export const toMinutes = value => {
  if (typeof value !== 'string') return null
  const [h, m] = value.split(':')
  const hours = Number(h)
  const minutes = Number(m)
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null
  return Math.min(MINUTES_IN_DAY, Math.max(0, hours * 60 + minutes))
}

export const formatHour = minutes => {
  const h = Math.floor(minutes / 60) % 24
  const suffix = h < 12 ? 'AM' : 'PM'
  const display = h % 12 === 0 ? 12 : h % 12
  return `${display} ${suffix}`
}

export const formatTime = minutes => {
  const h = Math.floor(minutes / 60) % 24
  const m = minutes % 60
  const suffix = h < 12 ? 'AM' : 'PM'
  const display = h % 12 === 0 ? 12 : h % 12
  return `${display}:${String(m).padStart(2, '0')} ${suffix}`
}

export const toSpan = session => {
  const start = toMinutes(session?.startTime)
  if (start === null) return null

  let end = toMinutes(session?.endTime)
  if (end === null || end <= start || end - start > MAX_DURATION_MIN) {
    end = start + DEFAULT_DURATION_MIN
  }
  return { start, end: Math.min(MINUTES_IN_DAY, end) }
}

export const getRange = sessions => {
  const spans = (sessions ?? []).map(toSpan).filter(Boolean)

  if (!spans.length) return { startMin: 8 * 60, endMin: 16 * 60 }

  let startMin = Math.floor(Math.min(...spans.map(s => s.start)) / 60) * 60
  let endMin = Math.ceil(Math.max(...spans.map(s => s.end)) / 60) * 60

  if (endMin - startMin < MIN_SPAN_MIN) endMin = startMin + MIN_SPAN_MIN
  if (endMin > MINUTES_IN_DAY) {
    endMin = MINUTES_IN_DAY
    startMin = Math.max(0, endMin - MIN_SPAN_MIN)
  }

  return { startMin, endMin }
}

export const clampTo = (range, minutes) => Math.min(range.endMin, Math.max(range.startMin, minutes))

export const toGeometry = (range, start, end) => {
  const top = clampTo(range, start)
  const bottom = clampTo(range, end)
  return { topHours: (top - range.startMin) / 60, heightHours: Math.max(0, bottom - top) / 60 }
}

export const assignLanes = entries => {
  const sorted = [...entries].sort((a, b) => a.start - b.start || b.end - a.end)

  const out = []
  let cluster = []
  let clusterEnd = -Infinity

  const flush = () => {
    if (!cluster.length) return

    const laneEnds = []
    for (const entry of cluster) {
      let lane = laneEnds.findIndex(end => end <= entry.start)
      if (lane === -1) {
        lane = laneEnds.length
        laneEnds.push(-Infinity)
      }
      laneEnds[lane] = entry.end
      entry.lane = Math.min(lane, MAX_LANES - 1)
    }

    const laneCount = Math.min(laneEnds.length, MAX_LANES)
    cluster.forEach(entry => {
      entry.laneCount = laneCount
    })

    out.push(...cluster)
    cluster = []
    clusterEnd = -Infinity
  }

  for (const entry of sorted) {
    if (entry.start >= clusterEnd) flush()
    cluster.push(entry)
    clusterEnd = Math.max(clusterEnd, entry.end)
  }
  flush()

  return out
}

export const buildLayout = (scheduledSessions, days) => {
  const sessions = scheduledSessions ?? []
  const range = getRange(sessions)

  const invalid = []
  const byDay = new Map(days.map(day => [day, []]))

  for (const session of sessions) {
    const span = toSpan(session)
    if (!span) {
      invalid.push(session)
      continue
    }
    const bucket = byDay.get(session.dayOfWeek)
    if (!bucket) continue

    bucket.push({
      session,
      ...span,
      ...toGeometry(range, span.start, span.end),
      conflict: session.list?.length > 1,
    })
  }

  const laidOut = new Map()
  for (const [day, entries] of byDay) laidOut.set(day, assignLanes(entries))

  const hourTicks = []
  for (let m = range.startMin; m <= range.endMin; m += 60) {
    hourTicks.push({ minutes: m, offsetHours: (m - range.startMin) / 60, label: formatHour(m) })
  }

  return { range, byDay: laidOut, hourTicks, invalid, hourSpan: (range.endMin - range.startMin) / 60 }
}

const place = (periods, range, predicate) =>
  (periods ?? [])
    .filter(predicate)
    .map(period => {
      const span = toSpan(period)
      if (!span) return null
      if (span.end <= range.startMin || span.start >= range.endMin) return null
      return { period, ...span, ...toGeometry(range, span.start, span.end) }
    })
    .filter(Boolean)

export const buildBands = (periods, range) =>
  place(periods, range, period => period.type !== 'course-session')

export const buildPeriodColumn = (periods, range, enrolledIds = null) => {
  const keep = period =>
    !enrolledIds || period.type !== 'course-session' || enrolledIds.has(String(period.id))

  const placed = place(periods, range, keep)

  const groups = new Map()
  for (const item of placed) {
    const key = `${item.start}-${item.end}`
    const group = groups.get(key)
    if (group) {
      group.periods.push(item.period)
      continue
    }
    groups.set(key, {
      key,
      start: item.start,
      end: item.end,
      topHours: item.topHours,
      heightHours: item.heightHours,
      periods: [item.period],
    })
  }

  return assignLanes([...groups.values()])
}

export const pxPerHourFor = (isPrinting, hourSpan) => {
  if (!isPrinting) return SCREEN_PX_PER_HOUR
  return Math.max(PRINT_MIN_PX_PER_HOUR, Math.min(SCREEN_PX_PER_HOUR, PRINT_MAX_BODY_PX / Math.max(1, hourSpan)))
}
